// src/pages/TransactionEntry/transactionEntrySave.js
import saveTransaction from "../../resources/transactions/transactionSave";

/**
 * Validate a single row.
 */
function validateRow(row) {
  const amountIn = Number(row.amountIn || 0);
  const amountOut = Number(row.amountOut || 0);

  if (!row.accountId) return "Account Head is required.";
  if (!row.date) return "Date is required.";
  if (!row.expenseType && !row.description)
    return "Either Type or Description is required.";
  if (amountIn === 0 && amountOut === 0)
    return "Either Amount In or Amount Out must be > 0.";

  return null;
}

/**
 * Save ALL rows — SAFE bulk mode
 * Does not override any browser properties.
 */
export async function saveAllTransactions(rows) {
  // Filter out empty rows
  const nonEmptyRows = rows.filter((r) => {
    const hasAmount =
      Number(r.amountIn || 0) !== 0 || Number(r.amountOut || 0) !== 0;
    return r.accountId || r.description || hasAmount || r.expenseType;
  });

  if (nonEmptyRows.length === 0) {
    throw new Error("Nothing to save. Enter at least one transaction.");
  }

  // -------------------------------
  // 1) Validate all rows
  // -------------------------------
  const rowErrors = {};
  nonEmptyRows.forEach((row) => {
    const err = validateRow(row);
    if (err) rowErrors[row.id] = err;
  });

  if (Object.keys(rowErrors).length > 0) {
    return { ok: false, rowErrors };
  }

  // -------------------------------
  // 2) Enable BULK MODE flag
  // -------------------------------
  window.__BULK_SAVE_MODE__ = true;

  let successCount = 0;
  let failureCount = 0;
  const successfulRowIds = [];
  const failedRows = [];

  try {
    // -------------------------------
    // 3) SAVE ALL ROWS SEQUENTIALLY
    // -------------------------------
    // Sequential saving is intentional because each row updates an account balance
    // and may also create a Visa mirror transaction.
    for (const row of nonEmptyRows) {
      try {
        const values = {
          accountId: row.accountId,
          date: row.date,
          expenseType: row.expenseType || "",
          description: row.description || "",
          amountIn: row.amountIn ? Number(row.amountIn) : 0,
          amountOut: row.amountOut ? Number(row.amountOut) : 0,
          paidBy: row.paidBy || "",
          comment: row.comment || "",
          proofFile: row.proofFile ? { rawFile: row.proofFile } : null,
          isMirror: false,
          id: undefined,
        };

        // saveTransaction() returns { success:false, error } on failure.
        // The old bulk logic ignored that return value and incorrectly counted
        // the row as saved. Always verify the result before successCount++.
        const result = await saveTransaction(values);

        if (!result?.success) {
          throw new Error(result?.error || "Transaction save failed.");
        }

        successCount++;
        successfulRowIds.push(row.id);
      } catch (err) {
        console.error("Row save failed:", err);
        failureCount++;
        failedRows.push({
          id: row.id,
          error: err?.message || "Unknown error",
        });
      }
    }
  } finally {
    // Always restore normal save mode even if an unexpected error occurs.
    window.__BULK_SAVE_MODE__ = false;
  }

  // -------------------------------
  // 4) Final message
  // -------------------------------
  if (failureCount === 0) {
    window.alert(`✅ Saved ${successCount} transaction(s) successfully.`);
  } else {
    window.alert(
      `⚠️ Saved ${successCount}, but ${failureCount} failed. Check errors.`
    );
  }

  return {
    ok: failureCount === 0,
    successCount,
    failureCount,
    successfulRowIds,
    failedRows,
  };
}
